import { forwardRef } from 'react';
import { FormNode } from '@/utils/api/aboboApiTypes';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import QuestionHeading from '@/components/QuestionHeading';

interface InformationalProps {
  node: FormNode;
}

export default forwardRef<any, InformationalProps>(function Informational(
  { node },
  ref
) {
  // Preprocess the markdown to handle HTML entities and spacing better
  const preprocessMarkdown = (text: string) => {
    return text
      // Convert &nbsp; to actual non-breaking spaces but preserve them as line breaks when multiple
      .replace(/&nbsp;\s*&nbsp;/g, '\n\n') // Multiple &nbsp; become paragraph breaks
      .replace(/&nbsp;/g, ' ') // Single &nbsp; becomes regular space
      // Clean up extra whitespace around horizontal rules
      .replace(/\s*---\s*/g, '\n\n---\n\n')
      // Ensure proper spacing around sections
      .replace(/\*\*([^*]+)\*\*\s*\n(?!\s*[-*])/g, '**$1**\n\n');
  };

  const processedHelperText = node.helperText ? preprocessMarkdown(node.helperText) : '';
  
  return (
    <div className="space-y-4" ref={ref}>
      <QuestionHeading required={node.metadata?.required}>
        {node.body}
      </QuestionHeading>

      {node.helperText && (
        <div className="text-md space-y-4 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            allowedElements={[
              'p',
              'strong',
              'em',
              'a',
              'ul',
              'ol',
              'li',
              'br',
              'hr',
              'code',
              'h1',
              'h2',
              'h3',
            ]}
            unwrapDisallowed
            components={{
              // Better paragraph handling with proper spacing
              p: ({ children, ...props }) => (
                <p className="mb-4 leading-relaxed text-foreground" {...props}>
                  {children}
                </p>
              ),
              // Better list styling
              ul: ({ children }) => (
                <ul className="list-disc pl-6 mb-6 space-y-1 text-foreground">
                  {children}
                </ul>
              ),
              li: ({ children }) => (
                <li className="leading-relaxed pl-1">
                  {children}
                </li>
              ),
              // Better horizontal rule styling - creates visual separation
              hr: () => (
                <div className="my-8 flex justify-center">
                  <div className="w-24 border-t border-border" />
                </div>
              ),
              // Better strong/bold styling
              strong: ({ children }) => (
                <strong className="font-semibold text-foreground mb-2">
                  {children}
                </strong>
              ),
              // Better code styling for email addresses and other code
              code: ({ children }) => (
                <code className="bg-muted px-3 py-2 rounded text-sm font-mono text-foreground block my-2 border border-border">
                  {children}
                </code>
              ),
              // Better heading styles
              h1: ({ children }) => (
                <h1 className="text-lg font-semibold text-foreground mb-4 mt-6 first:mt-0">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-base font-semibold text-foreground mb-3 mt-6 first:mt-0">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-sm font-semibold text-foreground mb-2 mt-4 first:mt-0">
                  {children}
                </h3>
              ),
              // Handle line breaks better
              br: () => <div className="h-4" />,
              a: ({ children, href }) => (
                <a
                  href={href}
                  className="underline hover:text-gray-500"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              ),
            }}
          >
            {processedHelperText}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
});
